import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Avatar
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Class as ClassIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { fetchJson } from '../../../utils/fetchJson';
import { usePhase } from '../../../contexts/PhaseContext';
import AppLoader from '../../../components/AppLoader';

const ClassReportsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  const { activePhase } = usePhase();
  const resolvedPhaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '').toString().trim().toLowerCase();
  const isSecPhase = resolvedPhaseCode === 'sec';
  const classMasterLabel = isSecPhase
    ? t('classManagement.reports.labels.classMaster', { defaultValue: 'Class Master' })
    : t('classManagement.reports.labels.classTeacher', { defaultValue: 'Class Teacher' });
  const classMasterNotAssignedLabel = isSecPhase
    ? t('classManagement.reports.classMasterNotAssigned', { defaultValue: 'No class master assigned' })
    : t('classManagement.reports.classTeacherNotAssigned', { defaultValue: 'No class teacher assigned' });
  const classMasterUnknownLabel = isSecPhase
    ? t('classManagement.reports.classMasterUnknown', { defaultValue: 'Unknown' })
    : t('classManagement.reports.classTeacherUnknown', { defaultValue: 'Unknown' });
  const getLevelLabel = (level) => {
    const normalizedLevel = (level || '').toString().trim().toLowerCase();
    if (normalizedLevel === 'nursery') return t('classManagement.add.levels.nursery', { defaultValue: 'Nursery' });
    if (normalizedLevel === 'primary') return t('classManagement.add.levels.primary', { defaultValue: 'Primary' });
    if (normalizedLevel === 'first_cycle') return t('classManagement.add.levels.firstCycle', { defaultValue: 'First Cycle' });
    if (normalizedLevel === 'second_cycle') return t('classManagement.add.levels.secondCycle', { defaultValue: 'Second Cycle' });
    return level || t('common.na', { defaultValue: 'N/A' });
  };
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    academicYear: '',
    status: 'all',
    searchTerm: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-refresh on phase/year change
  useEffect(() => {
    const handler = () => fetchData();
    window.addEventListener('app:phase:changed', handler);
    window.addEventListener('app:ay:changed', handler);
    return () => {
      window.removeEventListener('app:phase:changed', handler);
      window.removeEventListener('app:ay:changed', handler);
    };
  }, []);
  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activePhase]);

  const reqSeqRef = useRef(0);
  const prevCtxRef = useRef('');
  const fetchData = useCallback(async () => {
    const mySeq = ++reqSeqRef.current;
    const ctrl = new AbortController();
    setLoading(true);
    let maxGuard;
    try { maxGuard = setTimeout(() => { if (reqSeqRef.current === mySeq) setLoading(false); }, 2000); } catch(_) {}
    setError(null);
    try {
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || '';
      let phaseIdResolved = phaseId;
      if ((!phaseIdResolved || phaseIdResolved === 'null' || phaseIdResolved === 'undefined') && phaseCode) {
        try {
          const raw = localStorage.getItem('phaseCatalog');
          const arr = raw ? JSON.parse(raw) : [];
          if (Array.isArray(arr) && arr.length) {
            const found = arr.find(p => (p.code || '').toLowerCase() === String(phaseCode).toLowerCase());
            if (found && found.id) phaseIdResolved = String(found.id);
          }
        } catch (_) {}
      }
      // No-flash cache key
      const ctxKey = `${schoolId}|${phaseIdResolved || phaseCode || '-'}|${ayId || '-'}`;
      const cacheKeyRows = `classReports:rows:${ctxKey}`;
      const cacheKeySummary = `classReports:summary:${ctxKey}`;
      // If context changed and no cache, do not clear existing rows immediately (preserve last known values)
      let prefilled = false;
      if (prevCtxRef.current !== ctxKey) {
        prevCtxRef.current = ctxKey;
        // Prefill from cache if available
        try {
          const rawRows = localStorage.getItem(cacheKeyRows);
          if (rawRows) {
            const cachedRows = JSON.parse(rawRows);
            if (Array.isArray(cachedRows) && reqSeqRef.current === mySeq) { setClasses(cachedRows); if (cachedRows.length) prefilled = true; }
          }
        } catch (_) {}
        try {
          const rawSum = localStorage.getItem(cacheKeySummary);
          if (rawSum) {
            const cachedSum = JSON.parse(rawSum);
            if (cachedSum && typeof cachedSum === 'object' && reqSeqRef.current === mySeq) { setSummary(cachedSum); prefilled = true; }
          }
        } catch (_) {}
      } else {
        // Same context: still prefill if cache exists and current state is empty
        if (!classes?.length) {
          try {
            const rawRows = localStorage.getItem(cacheKeyRows);
            if (rawRows) {
              const cachedRows = JSON.parse(rawRows);
              if (Array.isArray(cachedRows) && reqSeqRef.current === mySeq) { setClasses(cachedRows); if (cachedRows.length) prefilled = true; }
            }
          } catch (_) {}
        }
        if (!summary) {
          try {
            const rawSum = localStorage.getItem(cacheKeySummary);
            if (rawSum) {
              const cachedSum = JSON.parse(rawSum);
              if (cachedSum && typeof cachedSum === 'object' && reqSeqRef.current === mySeq) { setSummary(cachedSum); prefilled = true; }
            }
          } catch (_) {}
        }
      }

      if (prefilled && reqSeqRef.current === mySeq) setLoading(false);

      const headers = schoolId ? { 'X-Scope-School-Id': schoolId } : {};
      const qPhase = phaseCode ? `?phase=${encodeURIComponent(phaseCode)}` : '';
      const summaryUrl = `/api/classes/summary-public-top?schoolId=${encodeURIComponent(schoolId)}${phaseIdResolved ? `&phaseId=${encodeURIComponent(phaseIdResolved)}` : qPhase}${ayId ? `&ayId=${encodeURIComponent(ayId)}` : ''}`;
      const [clsLite, sumRes, tch, yearsData, countsData] = await Promise.all([
        fetchJson(`/api/classes-lite${qPhase}`, { headers, signal: ctrl.signal }).catch(() => ({ classes: [] })),
        fetchJson(summaryUrl, { signal: ctrl.signal, skipAuth: true }).catch(() => null),
        // Teacher list may come from listUsers (paginated) or older flat arrays; request a large pageSize to include all staff
        fetchJson('/api/users?pageSize=500', { signal: ctrl.signal }).catch(() => []),
        fetchJson('/api/global-academic-years', { signal: ctrl.signal }).catch(() => ([])),
        (schoolId && phaseIdResolved && ayId)
          ? fetchJson(`/api/classes/enrollment-counts?schoolId=${encodeURIComponent(schoolId)}&phaseId=${encodeURIComponent(phaseIdResolved)}&ayId=${encodeURIComponent(ayId)}`, { signal: ctrl.signal }).catch(() => ({ counts: [] }))
          : Promise.resolve({ counts: [] })
      ]);

      const items = Array.isArray(clsLite?.classes) ? clsLite.classes : [];
      const counts = Array.isArray(countsData?.counts) ? countsData.counts : [];
      const cMap = new Map(counts.map(c => [Number(c.classId), Number(c.count) || 0]));
      const yearsList = Array.isArray(yearsData?.years) ? yearsData.years : (Array.isArray(yearsData) ? yearsData : []);
      const ayLabel = (yearsList.find(y => String(y.id) === String(ayId))?.name) || (yearsList.find(y => String(y.id) === String(ayId))?.label) || '';
      // Attach a numeric studentCount per class instead of constructing fake students arrays
      const rows = items.map(it => ({
        ...it,
        academicYear: ayLabel || '—',
        studentCount: cMap.get(Number(it.id)) || 0,
      }));

      if (reqSeqRef.current === mySeq) {
        setClasses(rows);
        setSummary(sumRes);

        // Normalize teachers list so we can reliably resolve names in the report
        // listUsers returns { total, page, rows: [...] }; older endpoints may return arrays or { users: [...] }
        let teachersArray = Array.isArray(tch)
          ? tch
          : (tch?.rows || tch?.users || tch?.data || []);
        teachersArray = (teachersArray || []).map(t => ({
          id: t.id ?? t.userId ?? t.staffId ?? t.teacherId ?? t.accountId,
          firstName: t.firstName ?? t.givenName ?? (t.name ? String(t.name).split(' ')[0] : ''),
          lastName: t.lastName ?? t.surname ?? (t.name ? String(t.name).split(' ').slice(1).join(' ') : ''),
          email: t.email || t.workEmail || '',
          avatarUrl: t.avatarUrl || null,
        })).filter(t => t.id != null);
        setTeachers(teachersArray);
        // Persist cache for no-flash experience on next context match
        try { localStorage.setItem(cacheKeyRows, JSON.stringify(rows)); } catch (_) {}
        try { if (sumRes) localStorage.setItem(cacheKeySummary, JSON.stringify(sumRes)); } catch (_) {}
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Error fetching data:', error);
      setError(t('classManagement.reports.errors.fetchData', { message: error?.message || '' }));
    } finally {
      if (reqSeqRef.current === mySeq) setLoading(false);
      if (maxGuard) { try { clearTimeout(maxGuard); } catch(_) {} }
    }
    return () => { try { ctrl.abort(); } catch (_) {} };
  }, [activePhase]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getFilteredClasses = () => {
    let filtered = [...classes];

    if (filters.academicYear) {
      filtered = filtered.filter(cls => cls.academicYear === filters.academicYear);
    }

    if (filters.status !== 'all') {
      if (filters.status === 'assigned') {
        filtered = filtered.filter(cls => cls.classMasterId);
      } else if (filters.status === 'unassigned') {
        filtered = filtered.filter(cls => !cls.classMasterId);
      }
    }

    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(cls => 
        cls.name.toLowerCase().includes(term)
      );
    }

    return filtered;
  };

  const getClassStats = () => {
    // Prefer server summary; fallback to client calc when unavailable
    if (summary) {
      return {
        totalClasses: summary.totalClasses || 0,
        assignedClasses: summary.assignedClasses || 0,
        unassignedClasses: summary.unassignedClasses || 0,
        totalStudents: summary.totalStudents || 0,
        averageStudentsPerClass: summary.avgStudentsPerClass || 0
      };
    }
    const filteredClasses = getFilteredClasses();
    return {
      totalClasses: filteredClasses.length,
      assignedClasses: filteredClasses.filter(c => c.classMasterId).length,
      unassignedClasses: filteredClasses.filter(c => !c.classMasterId).length,
      totalStudents: filteredClasses.reduce((sum, c) => sum + (Number(c.studentCount) || 0), 0),
      averageStudentsPerClass: filteredClasses.length
        ? (filteredClasses.reduce((sum, c) => sum + (Number(c.studentCount) || 0), 0) / filteredClasses.length).toFixed(1)
        : 0
    };
  };

  const getAcademicYears = () => {
    // If classes have academicYear label, use it; otherwise rely on YearSelector globally
    const years = [...new Set(classes.map(cls => cls.academicYear).filter(Boolean))];
    return years.sort().reverse();
  };

  const exportReport = async (format = 'csv') => {
    try {
      const filteredClasses = getFilteredClasses();
      
      if (format === 'csv') {
        const headers = [
          t('classManagement.reports.export.csvHeaders.className'),
          t('classManagement.reports.export.csvHeaders.academicYear'),
          classMasterLabel,
          t('classManagement.reports.export.csvHeaders.studentCount'),
          t('classManagement.reports.export.csvHeaders.createdDate'),
        ];
        const csvData = filteredClasses.map(cls => [
          cls.name,
          cls.academicYear,
          cls.classMasterId ? getTeacherName(cls.classMasterId) : classMasterNotAssignedLabel,
          Number(cls.studentCount) || 0,
          new Date(cls.createdAt).toLocaleDateString()
        ]);

        let csvContent = "data:text/csv;charset=utf-8,";
        csvContent += headers.join(",") + "\r\n";
        csvData.forEach(row => {
          csvContent += row.join(",") + "\r\n";
        });

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `class_report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      enqueueSnackbar(t('classManagement.reports.export.success', { format: format.toUpperCase() }), { variant: 'success' });
    } catch (error) {
      console.error('Error exporting report:', error);
      enqueueSnackbar(t('classManagement.reports.export.error', { message: error?.message || '' }), { variant: 'error' });
    }
  };

  const getTeacherName = (teacherId) => {
    if (!teacherId) return classMasterNotAssignedLabel;
    const teacher = (teachers || []).find(t => String(t.id) === String(teacherId));
    if (!teacher) return classMasterUnknownLabel;
    const first = teacher.firstName || teacher.givenName || (teacher.name ? String(teacher.name).split(' ')[0] : '');
    const last = teacher.lastName || teacher.surname || (teacher.name ? String(teacher.name).split(' ').slice(1).join(' ') : '');
    const full = `${first || ''} ${last || ''}`.trim() || teacher.name || teacher.email;
    return full || classMasterUnknownLabel;
  };

  const getClassStatus = (classItem) => {
    if (classItem.classMasterId) {
      return { label: t('classManagement.reports.statusChip.assigned'), color: 'success' };
    }
    return { label: t('classManagement.reports.statusChip.unassigned'), color: 'warning' };
  };

  // Only block if we truly have nothing to show yet
  if (loading && (!classes || classes.length === 0) && !summary) {
    return (
      <Box sx={{ p: 3 }}>
        <AppLoader
          variant="circular3"
          size={60}
          messages={[]}
        />
      </Box>
    );
  }

  const stats = getClassStats();
  const filteredClasses = getFilteredClasses();
  const academicYears = getAcademicYears();

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/class-management')}
          sx={{ mr: 2 }}
        >
          {t('classManagement.reports.back')}
        </Button>
        <Box>
          <Typography variant="h4" component="h1">
            {t('classManagement.reports.pageTitle')}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t('classManagement.reports.subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary">
                {stats.totalClasses}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('classManagement.reports.summaryCards.totalClasses')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="success.main">
                {stats.assignedClasses}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('classManagement.reports.summaryCards.assignedClasses')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="warning.main">
                {stats.unassignedClasses}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('classManagement.reports.summaryCards.unassignedClasses')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="info.main">
                {stats.totalStudents}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('classManagement.reports.summaryCards.totalStudents')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="secondary.main">
                {stats.averageStudentsPerClass}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('classManagement.reports.summaryCards.averagePerClass')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={t('classManagement.reports.filters.searchLabel')}
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              placeholder={t('classManagement.reports.filters.searchPlaceholder')}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('classManagement.reports.filters.academicYearLabel')}</InputLabel>
              <Select
                value={filters.academicYear}
                onChange={(e) => handleFilterChange('academicYear', e.target.value)}
                label={t('classManagement.reports.filters.academicYearLabel')}
              >
                <MenuItem value="">{t('classManagement.reports.filters.academicYearAll')}</MenuItem>
                {academicYears.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('classManagement.reports.filters.statusLabel')}</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label={t('classManagement.reports.filters.statusLabel')}
              >
                <MenuItem value="all">{t('classManagement.reports.filters.statusAll')}</MenuItem>
                <MenuItem value="assigned">{t('classManagement.reports.filters.statusAssigned')}</MenuItem>
                <MenuItem value="unassigned">{t('classManagement.reports.filters.statusUnassigned')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => fetchData()}
              variant="outlined"
              fullWidth
            >
              {t('classManagement.reports.filters.refresh')}
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => exportReport('csv')}
              variant="contained"
              fullWidth
            >
              {t('classManagement.reports.filters.exportCsv')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Classes Table */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 2 }}>
          <Typography variant="h6">
            {t('classManagement.reports.table.titleWithCount', { count: filteredClasses.length })}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('classManagement.reports.table.lastUpdated', { value: new Date().toLocaleString() })}
          </Typography>
        </Box>
        
        <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
          <Table sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('classManagement.reports.table.headers.class')}</TableCell>
                <TableCell>{t('classManagement.add.single.fields.level', { defaultValue: 'Level' })}</TableCell>
                <TableCell>{t('classManagement.reports.table.headers.academicYear')}</TableCell>
                <TableCell>{classMasterLabel}</TableCell>
                <TableCell>{t('classManagement.reports.table.headers.studentCount')}</TableCell>
                <TableCell>{t('classManagement.reports.table.headers.createdDate')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredClasses.map((classItem) => {
                const teacher = (teachers || []).find(t => String(t.id) === String(classItem.classMasterId));
                const displayName = classItem.classMasterId
                  ? getTeacherName(classItem.classMasterId)
                  : classMasterNotAssignedLabel;
                return (
                  <TableRow key={classItem.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ClassIcon sx={{ mr: 1, color: 'primary.main' }} />
                        {classItem.name}
                      </Box>
                    </TableCell>
                    <TableCell>{getLevelLabel(classItem.level)}</TableCell>
                    <TableCell>{classItem.academicYear}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {classItem.classMasterId && teacher ? (
                          <Avatar
                            sx={{ width: 28, height: 28, mr: 1, bgcolor: 'primary.main', fontSize: 14 }}
                            src={teacher.avatarUrl || undefined}
                          >
                            {(displayName || '?').charAt(0).toUpperCase()}
                          </Avatar>
                        ) : (
                          <PeopleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        )}
                        {displayName}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SchoolIcon sx={{ mr: 1, color: 'info.main' }} />
                        {Number(classItem.studentCount) || 0}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {new Date(classItem.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredClasses.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="textSecondary">
              {t('classManagement.reports.table.empty')}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ClassReportsPage;
